import { Link, useSearchParams } from 'react-router';
import { addDays, drillDown, spendAmount } from '@tahi/core';
import { Button, Money, ScreenHeader } from '../../../ui';
import { EntryRow } from '../components/EntryRow';
import { BREAKDOWN_BACK, copy, SPENDING_BACK } from '../copy';
import { parseDrill } from '../drill';
import { bucketLabel, categoryName, dayHeading, groupByDay, shortRange } from '../format';
import { useCategories, useEntries, useToday, useUncategorized, useWeekStart } from '../hooks';
import { ACCENT_LINK, LABEL, SCREEN } from '../styles';

export function EntryListScreen() {
  const [sp, setSp] = useSearchParams();
  const today = useToday();
  const weekStart = useWeekStart();
  const { filter, isDrill } = parseDrill(sp, today, weekStart);
  const entries = useEntries(filter.range);
  const categories = useCategories() ?? [];
  const uncategorized = useUncategorized() ?? [];

  if (entries === undefined) {
    return (
      <main className={`${SCREEN} md:max-w-2xl`}>
        <ScreenHeader title={copy.entriesTitle} back={SPENDING_BACK} />
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }

  const byId = new Map(categories.map((c) => [c.id, c]));
  const shown = drillDown(entries, filter);
  const groups = groupByDay(shown);
  const total = shown.reduce((s, e) => s + spendAmount(e), 0);
  const title =
    isDrill && filter.dim && filter.key !== undefined
      ? bucketLabel(filter.dim, filter.key, { categoryName: (id) => categoryName(id, byId) })
      : copy.entriesTitle;

  return (
    <main className={`${SCREEN} md:max-w-2xl`}>
      <div className="flex flex-col gap-1">
        <ScreenHeader
          title={title}
          // A drill-down comes from a Breakdown bar; the plain list belongs to Spending.
          back={isDrill ? BREAKDOWN_BACK : SPENDING_BACK}
          actions={
            <Link to="/m/spending/add" className={ACCENT_LINK}>
              {copy.add}
            </Link>
          }
        />
        {isDrill ? (
          <p className="text-sm text-muted">
            {shortRange(filter.range)} · <Money minor={total} />
          </p>
        ) : null}
      </div>

      {!isDrill && uncategorized.length > 0 ? (
        <Link
          to="/m/spending/tidy"
          className="min-h-11 text-sm text-muted underline underline-offset-4"
        >
          {copy.tidyLink(uncategorized.length)}
        </Link>
      ) : null}

      {groups.length === 0 ? (
        <p className="text-muted">{isDrill ? copy.drillEmpty : copy.empty}</p>
      ) : (
        <ul aria-label={copy.entriesTitle} className="flex flex-col gap-4">
          {groups.map((g) => (
            <li key={g.date}>
              <h2 className={`flex justify-between px-2 ${LABEL}`}>
                <span>{dayHeading(g.date, today)}</span>
                <Money minor={g.totalMinor} />
              </h2>
              <ul className="divide-y divide-divider">
                {g.entries.map((e) => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    categoryName={e.categoryId ? categoryName(e.categoryId, byId) : null}
                    today={today}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      {!isDrill ? (
        <Button
          variant="secondary"
          onClick={() => setSp({ start: addDays(filter.range.start, -60), end: filter.range.end })}
        >
          {copy.showOlder}
        </Button>
      ) : null}
    </main>
  );
}
